import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from 'contexts/AuthContext';
import { TrendingUp, Loader2 } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || '/api';

export default function InviteRegister() {
  const { t } = useTranslation();
  const { registerWithInvite } = useAuth();
  const navigate = useNavigate();
  const { token } = useParams();

  const [inviteInfo, setInviteInfo] = useState(null);
  const [validating, setValidating] = useState(true);
  const [inviteError, setInviteError] = useState('');

  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const inputClassName = "w-full px-3.5 py-2.5 border border-slate-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#1a4731]/25 focus:border-[#1a4731]/40 transition-all";
  const labelClassName = "block text-sm font-medium text-slate-600 mb-1.5";
  const primaryClassName = "w-full py-2.5 bg-[#1a4731] hover:bg-[#153a27] text-white font-semibold rounded-lg text-sm disabled:opacity-50 disabled:cursor-not-allowed transition-colors";

  useEffect(() => {
    const validateToken = async () => {
      try {
        const response = await axios.get(`${API_URL}/auth/register/invite/${token}/validate/`);
        setInviteInfo(response.data);
        if (response.data.email) {
          setEmail(response.data.email);
        }
      } catch (err) {
        const detail = err.response?.data?.detail || t('register.invite.invalidToken');
        setInviteError(detail);
      } finally {
        setValidating(false);
      }
    };
    validateToken();
  }, [token, t]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password !== passwordConfirm) {
      setError(t('register.passwordsMismatch'));
      return;
    }

    setLoading(true);
    try {
      await registerWithInvite(token, {
        username,
        email: email.trim(),
        password,
        password_confirm: passwordConfirm,
        first_name: firstName,
        last_name: lastName,
      });
      navigate('/dashboard', { replace: true });
    } catch (err) {
      const data = err.response?.data;
      if (data) {
        const messages = [];
        for (const value of Object.values(data)) {
          const vals = Array.isArray(value) ? value : [value];
          messages.push(...vals.map(v => (typeof v === 'string' ? v : JSON.stringify(v))));
        }
        setError(messages.join(' '));
      } else {
        setError(t('register.error'));
      }
    } finally {
      setLoading(false);
    }
  };

  if (validating) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f8f7f4] p-6">
        <div className="flex flex-col items-center">
          <Loader2 className="w-8 h-8 text-[#1a4731] animate-spin mb-4" />
          <p className="text-sm text-slate-400">{t('register.invite.validating')}</p>
        </div>
      </div>
    );
  }

  if (inviteError) {
    return (
      <div className="min-h-screen flex bg-[#f8f7f4]">
        <div className="hidden lg:flex lg:w-[45%] flex-col justify-between p-12 relative overflow-hidden bg-[#1a4731]">
          <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-white/5" />
          <div className="absolute -bottom-32 -left-16 w-80 h-80 rounded-full bg-white/5" />
          <div className="absolute top-1/2 right-16 w-44 h-44 rounded-full bg-white/[0.03]" />

          <div className="flex items-center gap-3 relative z-10">
            <div className="w-11 h-11 bg-white/15 backdrop-blur-sm rounded-xl flex items-center justify-center border border-white/10">
              <TrendingUp className="w-5 h-5 text-white" />
            </div>
            <span className="text-white font-bold text-xl tracking-tight">Carlo</span>
          </div>

          <div className="relative z-10">
            <h1 className="text-5xl font-bold text-white leading-[1.1] mb-5 tracking-tight">
              Smart<br />Restaurant<br />Operations
            </h1>
            <p className="text-white/50 text-lg leading-relaxed max-w-xs">
              Manage your sales, inventory, staff and analytics — all in one place.
            </p>
          </div>

          <p className="text-white/25 text-sm relative z-10">© {new Date().getFullYear()} Carlo</p>
        </div>

        <div className="flex-1 flex items-center justify-center p-6 sm:p-8">
          <div className="w-full max-w-[380px]">
            <div className="flex items-center gap-3 mb-10 lg:hidden">
              <div className="w-11 h-11 bg-[#1a4731] rounded-xl flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-white" />
              </div>
              <span className="font-bold text-xl text-slate-900 tracking-tight">Carlo</span>
            </div>

            <h2 className="text-2xl font-bold text-slate-900 mb-6">{t('register.invite.title')}</h2>
            <div className="bg-red-50 text-red-700 text-sm px-3.5 py-2.5 rounded-lg border border-red-100 mb-5">
              {inviteError}
            </div>
            <Link to="/login" className="text-[#1a4731] hover:text-[#153a27] font-medium text-sm transition-colors">
              {t('register.invite.goToLogin')}
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex bg-[#f8f7f4]">
      <div className="hidden lg:flex lg:w-[45%] flex-col justify-between p-12 relative overflow-hidden bg-[#1a4731]">
        <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-white/5" />
        <div className="absolute -bottom-32 -left-16 w-80 h-80 rounded-full bg-white/5" />
        <div className="absolute top-1/2 right-16 w-44 h-44 rounded-full bg-white/[0.03]" />

        <div className="flex items-center gap-3 relative z-10">
          <div className="w-11 h-11 bg-white/15 backdrop-blur-sm rounded-xl flex items-center justify-center border border-white/10">
            <TrendingUp className="w-5 h-5 text-white" />
          </div>
          <span className="text-white font-bold text-xl tracking-tight">Carlo</span>
        </div>

        <div className="relative z-10">
          <h1 className="text-5xl font-bold text-white leading-[1.1] mb-5 tracking-tight">
            Smart<br />Restaurant<br />Operations
          </h1>
          <p className="text-white/50 text-lg leading-relaxed max-w-xs">
            Manage your sales, inventory, staff and analytics — all in one place.
          </p>
        </div>

        <p className="text-white/25 text-sm relative z-10">© {new Date().getFullYear()} Carlo</p>
      </div>

      <div className="flex-1 flex items-center justify-center p-6 sm:p-8">
        <div className="w-full max-w-[420px]">
          <div className="flex items-center gap-3 mb-10 lg:hidden">
            <div className="w-11 h-11 bg-[#1a4731] rounded-xl flex items-center justify-center">
              <TrendingUp className="w-5 h-5 text-white" />
            </div>
            <span className="font-bold text-xl text-slate-900 tracking-tight">Carlo</span>
          </div>

          <h2 className="text-2xl font-bold text-slate-900 mb-1.5">{t('register.invite.title')}</h2>
          <p className="text-slate-400 text-sm mb-8">
            {t('register.invite.joiningOrg', { name: inviteInfo?.restaurant_name })}
          </p>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={labelClassName}>
                  {t('register.firstName')}
                </label>
                <input
                  type="text"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  className={inputClassName}
                />
              </div>
              <div>
                <label className={labelClassName}>
                  {t('register.lastName')}
                </label>
                <input
                  type="text"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  className={inputClassName}
                />
              </div>
            </div>

            <div>
              <label className={labelClassName}>
                {t('login.username')}
              </label>
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className={inputClassName}
                required
                autoFocus
                autoComplete="username"
              />
            </div>

            {inviteInfo?.email && (
              <div>
                <label className={labelClassName}>
                  {t('register.email')}
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className={`${inputClassName} bg-slate-50 text-slate-500`}
                  required
                  readOnly
                  autoComplete="email"
                />
              </div>
            )}

            <div>
              <label className={labelClassName}>
                {t('login.password')}
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className={inputClassName}
                required
                autoComplete="new-password"
              />
            </div>

            <div>
              <label className={labelClassName}>
                {t('register.confirmPassword')}
              </label>
              <input
                type="password"
                value={passwordConfirm}
                onChange={(e) => setPasswordConfirm(e.target.value)}
                className={inputClassName}
                required
                autoComplete="new-password"
              />
            </div>

            {error && (
              <div className="bg-red-50 text-red-700 text-sm px-3 py-2 rounded-lg border border-red-200">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className={primaryClassName}
            >
              {loading ? t('register.creating') : t('register.invite.joinAndCreate')}
            </button>
          </form>

          <p className="mt-6 text-center text-sm text-slate-400">
            {t('register.hasAccount')}{' '}
            <Link to="/login" className="text-[#1a4731] hover:text-[#153a27] font-medium transition-colors">
              {t('register.loginLink')}
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
